import React, { FC, useState, useCallback, ChangeEvent } from "react";
import Button from "../atoms/Button";
import InputField from "../atoms/InputField";
import Label from "../atoms/Label";
import Form from "../atoms/Form";
import TextArea from "../atoms/TextArea";
import SelectDropdown from "../atoms/SelectDropdown";
import Checkbox from "../atoms/Checkbox";
// A reusable form component that dynamically renders fields based on the provided configuration, 
// supports custom fields, validation, and handles form submission.

interface FieldConfig {
  name: string;
  type: string;
  label: string;
  placeholder?: string;
  options?: { name: string; value: string }[];
  disabled?: boolean;
  rows?: number;
  cols?: number;
  required?: boolean;
  component?: React.ComponentType<any>;
  props?: Record<string, any>;
  isLabelOnly?: boolean;
}

interface CommonFormProps {
  fields: FieldConfig[];
  values: Record<string, any>;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel?: () => void;
  isEdit?: boolean;
  children?: React.ReactNode;
  additionalFields?: FieldConfig[];
}

const CommonForm: FC<CommonFormProps> = ({
  fields,
  values,
  onChange,
  onSubmit,
  isEdit,
  children,
  onCancel,
  additionalFields = [],
}) => {
  const handleCustomChange = (name: string) => (value: string) => {
    const syntheticEvent = {
      target: { name, value },
    } as ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  const handleCheckboxChange = (name: string) => (checked: boolean) => {

    const syntheticEvent = new Event('change', { bubbles: true }) as unknown as ChangeEvent<HTMLInputElement>;
    Object.defineProperty(syntheticEvent, 'target', {
      value: {
        name,
        type: 'checkbox',
        checked,
        value: checked,
      },
      enumerable: true,
    });
    
    onChange(syntheticEvent);
  };

  const renderField = (field: FieldConfig) => {
    if (field.type === "custom" && field.component) {
      const CustomComponent = field.component;
      return (
        <CustomComponent
          value={values[field.name] || ""}
          onChange={handleCustomChange(field.name)}
          {...field.props}
        />
      );
    }

    if (field.type === "select" && field.options) {
      return (
        <SelectDropdown
          id={field.name}
          name={field.name}
          className="form-select"
          value={values[field.name] || ""}
          onChange={onChange}
          options={field.options}
          disabled={field.disabled}
        />
      );
    }

    if (field.type === "textarea") {
      return (
        <TextArea
          id={field.name}
          name={field.name}
          rows={field.rows || 3}
          cols={field.cols || 30}
          className="form-control"
          value={values[field.name] || ""}
          onChange={onChange}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
      );
    }

    if (field.type === "checkbox") {
      return (
        <Checkbox
       
          checked={!!values[field.name]}
          onChange={(checked: boolean) => handleCheckboxChange(field.name)(checked)}
          label={field.label}
        />
      );
    }

    return (
      <InputField
        id={field.name}
        name={field.name}
        type={field.type}
        value={values[field.name] || ""}
        onChange={onChange}
        placeholder={field.placeholder}
        disabled={field.disabled}
      />
    );
  };

  return (
    <Form onSubmit={onSubmit}>
      <div className="">
        {fields.map((field) => (
          <div key={field.name} className="mb-3">
            {field.type === "checkbox" ? (
              renderField(field)
            ) : (
              <>
                <Label htmlFor={field.name} required={field.required}>
                  {field.label}:
                </Label>
                {renderField(field)}
              </>
            )}

            {field.name === "Tool" && additionalFields.length > 0 && (
              <div className="additional-fields mt-2">
                {additionalFields.map((additionalField) => (
                  <div
                    key={additionalField.name || additionalField.label}
                    className="mb-3"
                  >
                    {additionalField.isLabelOnly ? (
                      <div className="fw-bold">{additionalField.label}</div>
                    ) : (
                      <>
                        <Label
                          htmlFor={additionalField.name}
                          required={additionalField.required}
                        >
                          {additionalField.label}:
                        </Label>
                        {renderField(additionalField)}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {children}
      </div>
      <div className="d-flex justify-content-end my-3">
        {isEdit && onCancel && (
          <Button
            className="btn btn-secondary me-2"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button className="btn SaveBtn" type="submit">
          {isEdit ? "Update" : "Save"}
        </Button>
      </div>
    </Form>
  );
};

export default CommonForm;